import React from "react"

import { useTranslation } from "react-i18next"

import { BulkAssignResult, BulkAssignRow } from "services/bonus/type"

type BulkAssignStatus = BulkAssignRow["status"]

interface OutcomeStyle {
  label: string
  cardBorder: string
  cardValue: string
  rowText: string
}

const OUTCOME_ORDER: BulkAssignStatus[] = ["assigned", "skipped", "failed"]

const OUTCOMES: Record<BulkAssignStatus, OutcomeStyle> = {
  assigned: {
    label: "bonus2.players.bulkAssigned",
    cardBorder: "border-emerald-600",
    cardValue: "text-emerald-400",
    rowText: "text-emerald-400"
  },
  skipped: {
    label: "bonus2.players.bulkSkipped",
    cardBorder: "border-gray-600",
    cardValue: "text-gray-300",
    rowText: "text-gray-400"
  },
  failed: {
    label: "bonus2.players.bulkFailed",
    cardBorder: "border-rose-600",
    cardValue: "text-rose-400",
    rowText: "text-rose-400"
  }
}

const COLUMNS = ["bonus2.players.player", "bonus2.players.outcome", "bonus2.players.reason"]

interface BulkAssignResultsProps {
  result: BulkAssignResult
  onReset: () => void
}

export const BulkAssignResults: React.FC<BulkAssignResultsProps> = ({ result, onReset }) => {
  const { t } = useTranslation("translation", { useSuspense: false })

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        {OUTCOME_ORDER.map((status) => (
          <div key={status} className={`bg-[#0C0C1A] border ${OUTCOMES[status].cardBorder} rounded p-4`}>
            <div className="text-xs text-gray-400">{t(OUTCOMES[status].label)}</div>
            <div className={`text-2xl font-bold ${OUTCOMES[status].cardValue}`}>{result[status]}</div>
          </div>
        ))}
      </div>

      {result.skipped > 0 && <p className="text-xs text-gray-400">{t("bonus2.players.bulkSkippedNote")}</p>}

      <div className="max-h-96 overflow-y-auto bg-[#0C0C1A] border border-gray-600 rounded">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-[#0C0C1A]">
            <tr className="text-left text-xs text-gray-400 border-b border-gray-700">
              {COLUMNS.map((column) => (
                <th key={column} className="px-4 py-2">
                  {t(column)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {result.rows.map((row) => (
              <tr key={row.userId} className="border-b border-gray-800 last:border-b-0">
                <td className="px-4 py-2 text-white">{row.username}</td>
                <td className="px-4 py-2">
                  <span className={OUTCOMES[row.status].rowText}>{t(OUTCOMES[row.status].label)}</span>
                </td>
                <td className="px-4 py-2 text-xs text-gray-400">
                  {row.code ? (t(`bonus2.errors.${row.code}`, { defaultValue: row.code }) as string) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button onClick={onReset} className="w-full px-6 py-3 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors">
        {t("bonus2.players.assignAnotherBonus")}
      </button>
    </div>
  )
}
